import "./Inventory.css";

import { LogoutDarkIcon } from "../../../../assets";
import { getInventoryViewHeading, OrganisationTypeConstants, ProjectIssuanceConstants } from "../../../../constants";
import { formatNumber, inventoryTooltipLabel } from "../../../../utils";
import {
  Column,
  CursorDataGrid,
  InventoryChartKey,
  InventorySelector,
  KanaTooltip,
  LoadingSpinner,
  MultiSelect,
  Row,
  StackedBarChart,
} from "../../../../widget";
import { useInventory } from "./useInventory";

const Inventory = (): JSX.Element => {
  const {
    hasData,
    hasEnoughDataForScrolling,
    gridOnChange,
    isReadableProject,
    isLoading,
    selectedUnitMeasurementData,
    activeSelectOptionText,
    handleChangeSelectOption,
    inventorySelectorButtons,
    pageData,
    suffix,
    columns,
    onTagsFilter,
    tagsFilterData,
    tagsFilterSelectedData,
    selectedStatuses,
    currentUserType,
    // eslint-disable-next-line no-undef
  } = useInventory();

  /*
    BR-02912: the heading names the active statuses in the canonical order Issued, Draft, Inactive,
    never the order they were clicked, so it does not reshuffle as the user selects.
  */
  const statusLabel = [
    ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED,
    ProjectIssuanceConstants.ISSUANCE_STATUS_DRAFT,
    ProjectIssuanceConstants.ISSUANCE_STATUS_INACTIVE,
  ]
    .filter((status) => selectedStatuses.includes(status))
    .join(", ");

  return (
    <div className="TemplateTabCard LandholdingInventory">
      <div className="InventoryHeaderContainer">
        <div className="InventoryHeaderTitle">
          <h2>{`${selectedUnitMeasurementData === "" ? "Overall" : selectedUnitMeasurementData} inventory: ${getInventoryViewHeading(activeSelectOptionText).toLowerCase()}${statusLabel ? ` (${statusLabel})` : ""}`}</h2>
          <KanaTooltip
            tooltipHeader="Inventory:"
            tooltipText="These figures cover the issuance statuses shown. Change which statuses are included from the Inventory card above."
          />
        </div>
        {/* Gated on the options themselves, not on the measurement: Overall has no view options,
            and neither does any measurement the selector has no buttons for - a bare "Display"
            label with nothing beside it is worse than no label. */}
        {inventorySelectorButtons.length > 0 && (
          <div className="HeaderButtons">
            <h3>Display</h3>
            <InventorySelector
              buttons={inventorySelectorButtons}
              activeSelector={activeSelectOptionText}
              onChange={handleChangeSelectOption}
              disabled={isLoading}
            />
          </div>
        )}
      </div>
      {(hasData || isLoading) && (
        <Row spacingV="s">
          <Column span={12}>
            <div className="InventoryChartContainer">
              {isLoading ? (
                <div className="InventoryChartLoading">
                  <LoadingSpinner type="blue" size="medium" />
                </div>
              ) : (
                <>
                  <StackedBarChart
                    xLabels={pageData.chartLabels}
                    data={pageData.chartData ?? []}
                    hasYAxisGridLines
                    hasYAxisTicks
                    showLegend={false}
                    // D-4 - Nutrient Neutrality quantities are an annual rate, so they carry their unit.
                    customTooltipLabel={(context) => inventoryTooltipLabel(context, undefined, suffix)}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    yAxisTickCallbackFunction={(val: any) => `${formatNumber(val)}${suffix ?? ""}`}
                  />
                  <InventoryChartKey
                    surface="dashboard"
                    unitMeasurement={selectedUnitMeasurementData}
                    isSalesOverTime={false}
                    showAllocatedAndAvailable={!isReadableProject}
                    nutrients={pageData.chartKeyNutrients}
                  />
                </>
              )}
            </div>
          </Column>
        </Row>
      )}
      {hasData && (
        <div className="InventoryFilters">
          <h3>Filter by</h3>
          <MultiSelect
            label=" "
            className="TagsFilter"
            placeholder="Tags"
            data={tagsFilterData}
            onChange={(keys) => onTagsFilter(keys)}
            disabled={isLoading}
          />
        </div>
      )}
      <Row spacingV={hasEnoughDataForScrolling ? "3xl" : undefined}>
        <Column span={12}>
          {/* The "Filter by" controls only render when there is data, so only indent the
              filter row to clear them in that case. */}
          <div
            className={`InventoryDataGrid ${hasEnoughDataForScrolling ? "Scrollable" : ""} ${hasData ? "HasFilters" : ""}`}
          >
            <CursorDataGrid
              key={
                selectedUnitMeasurementData +
                activeSelectOptionText +
                tagsFilterSelectedData +
                selectedStatuses.join(",")
              }
              onChange={gridOnChange}
              columns={columns}
              clientSide={false}
              filterable
              pagination={{ pageSize: 1000 }}
              dataIsLoading={isLoading}
              data={[]}
              noDataMessage="No inventory data to display"
              exportable
              exportButtonText="Export table"
              exportButtonIcon={<LogoutDarkIcon className="ExportButtonIcon" />}
              exportFileName=""
              infiniteScroll
              hasTotalRow
              totalColumnNames={
                isReadableProject
                  ? ["availableUnits", "reservedUnits", "quotedUnits", "soldUnits"]
                  : ["allocatedUnits", "availableUnits"]
              }
              variant={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "default"}
            />
          </div>
        </Column>
      </Row>
    </div>
  );
};

export default Inventory;
